// El logo de Academia Alpha: los números, en un solo sitio.
//
// POR QUE ESTO EXISTE APARTE DEL COMPONENTE. Había TRES marcas distintas conviviendo y nadie
// lo había notado: los PDF llevaban en la portada un logo con dos picos que se leía como una
// M, la web dibujaba un pico suelto con una raya debajo, y el logo que Gabri enseña por ahí
// era un tercero. Ninguno de los tres sabía de los otros dos, porque cada uno estaba dibujado
// a mano en su fichero.
//
// Aquí están los trazos y nada más. El componente de React los pinta como elementos, el
// generador de PNG los pinta como texto, y los dos dibujan lo mismo porque leen lo mismo.
// `tools/check_logo.py` vigila que el PNG de los PDF se haya regenerado cuando esto cambie:
// un logo nuevo en la web y el viejo en los PDF es la misma avería de siempre volviendo.
//
// LA FORMA: dos galones abiertos, uno dentro de otro. Se lee ALPHA, no una M —que era el
// problema del original— y no una montaña. Sin base a propósito: la base la tenía el logo
// intermedio y es lo que lo convertía en un triángulo cerrado en vez de en una letra.

export const CAJA = { ancho: 120, alto: 104 };

// Medidos sobre el logo de referencia: alto entre semiancho ≈ 1.7, el galón interior arranca
// al 51% de la altura del exterior y su semiancho es el 38% del de fuera.
export const FUERA = "M 8 96 L 60 8 L 112 96";
export const DENTRO = "M 41 86 L 60 52 L 79 86";

export const ROJO = "#D10000";
export const NEGRO = "#0B0B0B";
export const BLANCO = "#FFFFFF";

/**
 * Las tres capas del trazo, de atrás a delante: el humo, la brasa y la luz.
 *
 * SOBRE CLARO EL HUMO SE BAJA, y no es un capricho: el mismo halo que sobre negro parece
 * fuego, sobre blanco es una mancha rosa alrededor de la marca. Es el mismo dibujo con la
 * misma alma y distinta cantidad de aire.
 *
 * Y A TAMAÑO PEQUEÑO EL NUCLEO ENGORDA. Un galón abierto de 1,9 de grosor a 16 píxeles —el
 * favicon— desaparece: queda un rasguño. Es lo que en tipografía se llama tamaño óptico, y
 * aquí es la diferencia entre tener icono y no tenerlo.
 */
export function capas({ oscuro = true, pequeno = false } = {}) {
  const core = oscuro ? BLANCO : NEGRO;
  return {
    core,
    humo: {
      opacidad: oscuro ? 0.85 : 0.42,
      fuera: pequeno ? 6 : 5,
      dentro: pequeno ? 5 : 4,
      desenfoque: pequeno ? [5, 2.2] : [7, 3],
    },
    brasa: {
      opacidad: oscuro ? 0.9 : 0.72,
      fuera: pequeno ? 4.2 : 3.4,
      dentro: pequeno ? 3.6 : 2.8,
    },
    luz: {
      desenfoque: pequeno ? 1 : 1.5,
      fuera: pequeno ? 2.8 : 1.9,
      dentro: pequeno ? 2.4 : 1.6,
    },
  };
}

/**
 * El mismo dibujo como texto, para lo que no es React: el PNG de los PDF y el favicon.
 *
 * `cuadrado` PONE LA MARCA EN UN LIENZO 1:1 con aire alrededor, y no es un lujo: la portada
 * del PDF dibuja el logo en un cuadro de 96×96 y el favicon es cuadrado por definición.
 * Metiendo un dibujo de 120×104 en un cuadrado, el navegador y reportlab lo ESTIRAN —no
 * avisan, lo estiran— y el galón sale gordo y bajo. Se centra aquí, en el origen, y no en
 * cada sitio que lo use.
 */
export function svgDeAlpha({ ancho = 120, oscuro = true, pequeno = false, fondo = "",
                             cuadrado = false, aire = 0.14 } = {}) {
  const c = capas({ oscuro, pequeno });
  let vb = `0 0 ${CAJA.ancho} ${CAJA.alto}`;
  let alto = Math.round((ancho * CAJA.alto) / CAJA.ancho);
  let fondoW = CAJA.ancho, fondoH = CAJA.alto, fondoX = 0, fondoY = 0;
  if (cuadrado) {
    const lado = Math.round(Math.max(CAJA.ancho, CAJA.alto) * (1 + aire * 2));
    fondoX = -(lado - CAJA.ancho) / 2;
    fondoY = -(lado - CAJA.alto) / 2;
    vb = `${fondoX} ${fondoY} ${lado} ${lado}`;
    alto = ancho;
    fondoW = lado;
    fondoH = lado;
  }
  const rect = fondo
    ? `<rect x="${fondoX}" y="${fondoY}" width="${fondoW}" height="${fondoH}" fill="${fondo}"/>`
    : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${ancho}" height="${alto}" `
    + `viewBox="${vb}" fill="none">`
    + `<defs>`
    + `<filter id="humo" x="-80%" y="-80%" width="260%" height="260%">`
    + `<feGaussianBlur stdDeviation="${c.humo.desenfoque[0]}" result="a"/>`
    + `<feGaussianBlur stdDeviation="${c.humo.desenfoque[1]}" in="SourceGraphic" result="b"/>`
    + `<feMerge><feMergeNode in="a"/><feMergeNode in="a"/><feMergeNode in="b"/></feMerge></filter>`
    + `<filter id="luz" x="-60%" y="-60%" width="220%" height="220%">`
    + `<feGaussianBlur stdDeviation="${c.luz.desenfoque}" result="d"/>`
    + `<feMerge><feMergeNode in="d"/><feMergeNode in="SourceGraphic"/></feMerge></filter>`
    + `</defs>${rect}`
    + `<g filter="url(#humo)" opacity="${c.humo.opacidad}">`
    + `<path d="${FUERA}" stroke="${ROJO}" stroke-width="${c.humo.fuera}" stroke-linejoin="miter" fill="none"/>`
    + `<path d="${DENTRO}" stroke="${ROJO}" stroke-width="${c.humo.dentro}" stroke-linejoin="miter" fill="none"/></g>`
    + `<path d="${FUERA}" stroke="${ROJO}" stroke-width="${c.brasa.fuera}" stroke-linejoin="miter" fill="none" opacity="${c.brasa.opacidad}"/>`
    + `<path d="${DENTRO}" stroke="${ROJO}" stroke-width="${c.brasa.dentro}" stroke-linejoin="miter" fill="none" opacity="${c.brasa.opacidad}"/>`
    + `<g filter="url(#luz)">`
    + `<path d="${FUERA}" stroke="${c.core}" stroke-width="${c.luz.fuera}" stroke-linejoin="miter" fill="none"/>`
    + `<path d="${DENTRO}" stroke="${c.core}" stroke-width="${c.luz.dentro}" stroke-linejoin="miter" fill="none"/></g>`
    + `</svg>`;
}
